import { ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  className?: string
  onClick?: () => void
}

const Card = ({ children, className = '', onClick }: CardProps) => {
  return (
    <div
      className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

// Modal overlay con Card
interface CardModalProps {
  children: ReactNode
  visible?: boolean
  onClose?: () => void
}

export const CardModal = ({ children, visible = true, onClose }: CardModalProps) => {
  if (!visible) return null

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-pop"
      onClick={onClose}
    >
      <div 
        className="max-w-sm mx-4 text-center bg-white rounded-2xl shadow-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

// Game result modal
interface GameResultProps {
  isWin: boolean
  score: number
  level: number
  lastHarvest?: {
    icon: string
    size: number
    isCombo: boolean
    score: number
  } | null
  onPlayAgain: () => void
  onNextLevel?: () => void
}

export const GameResultModal = ({
  isWin,
  score,
  level,
  lastHarvest,
  onPlayAgain,
  onNextLevel,
}: GameResultProps) => {
  return (
    <CardModal>
      <div className="space-y-4">
        {isWin ? (
          <>
            <span className="text-5xl">🎉</span>
            <h2 className="font-title text-2xl text-green">Level Complete!</h2>
            <p className="font-title text-xl">⭐️ Level {level + 1}</p>
          </>
        ) : (
          <>
            <span className="text-5xl">💥</span>
            <h2 className="font-title text-2xl text-fire">Game Over...</h2>
          </>
        )}

        <div className="flex items-center justify-center gap-2">
          <span className="text-2xl">🔥</span>
          <span className="text-gray-500">Score</span>
          <span className="font-title text-xl">{score}</span>
        </div>

        {lastHarvest?.isCombo && (
          <p className="text-fire font-semibold">
            💣 +{lastHarvest.size - 6} moves bonus!
          </p>
        )}

        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={onPlayAgain}
            className="flex items-center gap-2 px-4 py-2 bg-blue text-white rounded-xl font-title hover:bg-ocean transition-colors"
          >
            🔄 <span>Play Again</span>
          </button>
          
          {isWin && onNextLevel && (
            <button
              onClick={onNextLevel}
              className="flex items-center gap-2 px-4 py-2 bg-grass text-white rounded-xl font-title hover:bg-green transition-colors"
            >
              💪 <span>Next Level</span>
            </button>
          )}
        </div>
      </div>
    </CardModal>
  )
}

// Combo notification
interface ComboNotificationProps {
  icon: string
  size: number
  score: number
  isCombo: boolean
}

export const ComboNotification = ({ icon, size, score, isCombo }: ComboNotificationProps) => {
  return (
    <CardModal>
      <div className="space-y-2">
        {isCombo && (
          <p className="font-title text-xl text-fire">💥 Strike {size}!</p>
        )}
        <p className="text-3xl">
          {Array(Math.min(size, 10)).fill(icon).join('')}
        </p>
        <p className="font-title text-lg text-flame">
          +{score} 🔥
        </p>
        {isCombo && (
          <p className="text-sm text-gray-500">
            💣 +{size - 6} moves
          </p>
        )}
      </div>
    </CardModal>
  )
}

export default Card
